import { db, auth, storage } from './config';
import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  updateDoc,
  setDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  startAfter,
  deleteField,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, uploadBytesResumable, deleteObject, listAll } from 'firebase/storage';

// Upload photo to Firebase Storage with progress tracking
export const uploadActivityPhoto = async (file, userId, activityId, onProgress) => {
  try {
    if (!file) return null;

    // Create a unique filename using userId, activityId, and timestamp
    const timestamp = Date.now();
    const fileName = `activities/${userId}/${activityId}_${timestamp}.jpg`;

    // Create a storage reference
    const storageRef = ref(storage, fileName);

    // Upload with progress tracking
    if (onProgress) {
      return new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(storageRef, file);

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress(progress);
          },
          (error) => {
            console.error('Firebase Storage Error: Failed to upload photo.', error);
            reject(error);
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve(downloadURL);
          }
        );
      });
    } else {
      // Upload without progress tracking
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    }
  } catch (error) {
    console.error('Firebase Storage Error: Failed to upload photo.', error);
    throw error;
  }
};

// Upload both full-size and thumbnail images
export const uploadActivityPhotos = async (fullSizeFile, thumbnailFile, userId, activityId, onProgress) => {
  try {
    if (!fullSizeFile || !thumbnailFile) return { fullSizeUrl: null, thumbnailUrl: null };

    const timestamp = Date.now();

    // Upload full-size image
    const fullSizeRef = ref(storage, `activities/${userId}/${activityId}_${timestamp}_full.jpg`);
    const thumbnailRef = ref(storage, `activities/${userId}/${activityId}_${timestamp}_thumb.jpg`);

    // Upload full-size with progress (0-70%)
    const fullSizeUrl = await new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(fullSizeRef, fullSizeFile);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 70;
          if (onProgress) onProgress(progress);
        },
        (error) => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        }
      );
    });

    // Upload thumbnail (70-100%)
    const thumbnailUrl = await new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(thumbnailRef, thumbnailFile);

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = 70 + (snapshot.bytesTransferred / snapshot.totalBytes) * 30;
          if (onProgress) onProgress(progress);
        },
        (error) => reject(error),
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          resolve(url);
        }
      );
    });

    return { fullSizeUrl, thumbnailUrl };
  } catch (error) {
    console.error('Firebase Storage Error: Failed to upload photos.', error);
    throw error;
  }
};

// Get user's causes
export const getUserCauses = async (userId) => {
  try {
    const q = query(
      collection(db, 'userCauses'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    const causes = snapshot.docs.map(doc => ({ ...doc.data(), docId: doc.id }));
    return causes;
  } catch (error) {
    console.error('Firebase Error: Failed to fetch user causes.', error);
    throw error;
  }
};

// Add user feedback
export const addFeedback = async (feedbackData) => {
  try {
    const currentUser = auth.currentUser;

    const feedbackWithMetadata = {
      ...feedbackData,
      userId: currentUser?.uid || null,
      userEmail: currentUser?.email || null,
      status: 'new',
      priority: null,
      adminNotes: '',
      resolvedDate: null,
      appVersion: '1.0.0',
      device: navigator.userAgent,
      timestamp: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'feedback'), feedbackWithMetadata);
    return { id: docRef.id, ...feedbackWithMetadata };
  } catch (error) {
    console.error('Firebase Error: Failed to add feedback.', error);
    throw error;
  }
};

// Get user's recent activities
export const getRecentActivities = async (userId, limitCount = 5, lastVisible = null) => {
  try {
    let q = query(
      collection(db, 'activities'),
      where('userId', '==', userId),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    );

    if (lastVisible) {
      q = query(
        collection(db, 'activities'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        startAfter(lastVisible),
        limit(limitCount)
      );
    }
    
    const snapshot = await getDocs(q);
    const activities = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const lastDoc = snapshot.docs[snapshot.docs.length - 1];
    
    return { activities, lastVisible: lastDoc };
  } catch (error) {
    console.error('Firebase Error: Failed to fetch recent activities.', error);
    throw error;
  }
};

// Log a new activity with optional images
export const logActivity = async (activityData, processedImages = null, onProgress) => {
  try {
    // First, create the activity document without the image URLs
    const activityWithTimestamp = {
      ...activityData,
      imageUrl: null, // Full-size image
      thumbnailUrl: null, // Thumbnail image
      timestamp: serverTimestamp()
    };

    const docRef = await addDoc(collection(db, 'activities'), activityWithTimestamp);

    // If there are processed images, upload them
    if (processedImages && processedImages.fullSize && processedImages.thumbnail) {
      const { fullSizeUrl, thumbnailUrl } = await uploadActivityPhotos(
        processedImages.fullSize,
        processedImages.thumbnail,
        activityData.userId,
        docRef.id,
        onProgress
      );

      await updateDoc(doc(db, 'activities', docRef.id), {
        imageUrl: fullSizeUrl,
        thumbnailUrl: thumbnailUrl
      });

      return { id: docRef.id, ...activityWithTimestamp, imageUrl: fullSizeUrl, thumbnailUrl };
    }

    return { id: docRef.id, ...activityWithTimestamp };
  } catch (error) {
    console.error('Firebase Error: Failed to log activity.', error);
    throw error;
  }
};

// Add a new cause for a user
export const addUserCause = async (userId, causeData) => {
  try {
    const causeWithUserId = { ...causeData, userId };
    const docRef = await addDoc(collection(db, 'userCauses'), causeWithUserId);
    return { id: docRef.id, ...causeWithUserId };
  } catch (error) {
    console.error('Firebase Error: Failed to add user cause.', error);
    throw error;
  }
};

// Remove a user's cause
export const removeUserCause = async (causeId) => {
  try {
    const causeRef = doc(db, 'userCauses', causeId);
    await deleteDoc(causeRef);
  } catch (error) {
    console.error('Firebase Error: Failed to remove user cause.', error);
    throw error;
  }
};

// Get activity details
export const getActivityById = async (activityId) => {
  try {
    const docRef = doc(db, 'activities', activityId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      throw new Error('Activity not found');
    }
  } catch (error) {
    console.error('Error fetching activity:', error);
    throw error;
  }
};

// Update feedback status (admin function)
export const updateFeedbackStatus = async (feedbackId, status) => {
  try {
    const feedbackRef = doc(db, 'feedback', feedbackId);
    const updates = { status };

    // If status is 'resolved', add resolvedDate
    if (status === 'resolved') {
      updates.resolvedDate = serverTimestamp();
    }

    await updateDoc(feedbackRef, updates);
    return { success: true };
  } catch (error) {
    console.error('Firebase Error: Failed to update feedback status.', error);
    throw error;
  }
};

// Update feedback priority (admin function)
export const updateFeedbackPriority = async (feedbackId, priority) => {
  try {
    const feedbackRef = doc(db, 'feedback', feedbackId);
    await updateDoc(feedbackRef, { priority });
    return { success: true };
  } catch (error) {
    console.error('Firebase Error: Failed to update feedback priority.', error);
    throw error;
  }
};

// Get user's feedback
export const getUserFeedback = async (userId) => {
  try {
    const q = query(
      collection(db, 'feedback'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    const feedback = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    // Sort by timestamp on client side to avoid needing composite index
    feedback.sort((a, b) => {
      const aTime = a.timestamp?.toDate?.() || new Date(0);
      const bTime = b.timestamp?.toDate?.() || new Date(0);
      return bTime - aTime; // Newest first
    });
    return feedback;
  } catch (error) {
    console.error('Firebase Error: Failed to fetch user feedback.', error);
    throw error;
  }
};

// Upload share image to Firebase Storage and return public URL
export const uploadShareImage = async (imageBlob, userId, activityId) => {
  try {
    const filename = `share_${activityId}_${Date.now()}.png`;
    const storageRef = ref(storage, `shareImages/${userId}/${filename}`);

    // Upload the blob
    await uploadBytes(storageRef, imageBlob);

    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);

    return downloadURL;
  } catch (error) {
    console.error('Firebase Error: Failed to upload share image.', error);
    throw error;
  }
};

// ─── PUBLIC SHARE PAGES (Release 14) ───────────────────────────────────────
// A publicShares doc is a snapshot of exactly what the share card shows.
// It backs lightway-app.lightandcover.org/s/{shareId} (see functions/sharePage).

export const createPublicShare = async ({
  userId, activityId, title, causeTitle, levelTitle, reflection, displayName, imageUrl, photoUrl,
}) => {
  try {
    const docRef = await addDoc(collection(db, 'publicShares'), {
      userId,
      activityId: activityId || '',
      title: (title || '').slice(0, 200),
      causeTitle: (causeTitle || '').slice(0, 100),
      levelTitle: (levelTitle || '').slice(0, 100),
      // Reflections can be ~2000 chars; the sharePage function clips the og:description
      // to 180 chars with a word boundary, but the full text is shown on the share page.
      reflection: (reflection || '').slice(0, 2000),
      displayName: (displayName || 'Lightway Advocate').slice(0, 80),
      imageUrl,
      photoUrl: photoUrl || '',
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Firebase Error: Failed to create public share.', error);
    throw error;
  }
};

// Remove public share pages for an activity (called when the activity is deleted)
export const deletePublicSharesForActivity = async (userId, activityId) => {
  if (!userId || !activityId) return;
  try {
    const q = query(
      collection(db, 'publicShares'),
      where('userId', '==', userId),
      where('activityId', '==', activityId)
    );
    const snap = await getDocs(q);
    await Promise.all(snap.docs.map(async (d) => {
      const { imageUrl } = d.data();
      if (imageUrl) {
        try {
          await deleteObject(ref(storage, imageUrl));
        } catch (e) {
          // Image may already be gone
        }
      }
      await deleteDoc(d.ref);
    }));
  } catch (error) {
    console.error('Firebase Error: Failed to delete public shares.', error);
    // Non-blocking: activity deletion should still succeed
  }
};

// Track share analytics
export const trackShare = async (shareData) => {
  try {
    await addDoc(collection(db, 'shares'), {
      ...shareData,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Firebase Error: Failed to track share.', error);
    // Fail silently - don't block user action
  }
};

// ─── ELEVATION FUNCTIONS ───────────────────────────────────────────────────

// Submit a level elevation request and update the userCause level
export const submitElevationRequest = async (userId, causeData, toLevel, reflection) => {
  try {
    const levelMap = {
      streetlight: { id: 'streetlight', title: 'Streetlight', description: 'Personal acts of awareness and care' },
      daylight: { id: 'daylight', title: 'Daylight', description: 'Collaborative community work' },
      sunshield: { id: 'sunshield', title: 'Sunshield', description: 'Strategic systems change advocacy' }
    };

    // 1. Log the elevation in its own collection
    await addDoc(collection(db, 'elevations'), {
      userId,
      causeId: causeData.docId,
      causeName: causeData.title,
      fromLevel: causeData.level.title,
      toLevel: levelMap[toLevel].title,
      reflection: reflection || '',
      requestedAt: serverTimestamp()
    });

    // 2. Update the userCause document with the new level
    const causeRef = doc(db, 'userCauses', causeData.docId);
    await updateDoc(causeRef, {
      level: levelMap[toLevel]
    });

    return { success: true, newLevel: levelMap[toLevel] };
  } catch (error) {
    console.error('Firebase Error: Failed to submit elevation request.', error);
    throw error;
  }
};

// ─── MILESTONE FUNCTIONS ───────────────────────────────────────────────────

// Check which milestones a user has already earned
export const getUserMilestones = async (userId) => {
  try {
    const q = query(
      collection(db, 'milestones'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data().milestoneKey);
  } catch (error) {
    console.error('Firebase Error: Failed to fetch milestones.', error);
    return [];
  }
};

// Award a milestone (call after checking it hasn't been earned yet)
export const awardMilestone = async (userId, milestoneKey) => {
  try {
    await addDoc(collection(db, 'milestones'), {
      userId,
      milestoneKey,
      achievedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Firebase Error: Failed to award milestone.', error);
    return false;
  }
};

// Get activity count for a user (used for milestone checks)
export const getUserActivityCount = async (userId) => {
  try {
    const q = query(
      collection(db, 'activities'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Firebase Error: Failed to count activities.', error);
    return 0;
  }
};

// Helper to extract level name from various formats
const extractLevelName = (levelData) => {
  if (!levelData) return null;

  let levelStr = null;

  // Get the string to parse
  if (typeof levelData === 'string') {
    levelStr = levelData.toLowerCase();
  } else if (levelData.title) {
    levelStr = levelData.title.toLowerCase();
  } else if (levelData.id && typeof levelData.id === 'string') {
    levelStr = levelData.id.toLowerCase();
  }

  if (!levelStr) return null;

  // Check for level keywords (handles both "Streetlight" and "Level 1 - Streetlight Advocate")
  if (levelStr.includes('streetlight') || levelStr.includes('level 1')) return 'streetlight';
  if (levelStr.includes('daylight') || levelStr.includes('level 2')) return 'daylight';
  if (levelStr.includes('sunshield') || levelStr.includes('level 3')) return 'sunshield';

  return null;
};

// Get the last activity date for a specific cause
export const getLastActivityByCause = async (userId, causeTitle) => {
  const q = query(
    collection(db, 'activities'),
    where('userId', '==', userId),
    where('cause.title', '==', causeTitle),
    orderBy('timestamp', 'desc'),
    limit(1)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  return snapshot.docs[0].data().timestamp;
};

// Get stats for profile display (count by level)
export const getUserActivityStats = async (userId) => {
  try {
    const q = query(
      collection(db, 'activities'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    const stats = { streetlight: 0, daylight: 0, sunshield: 0, total: 0 };

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      stats.total++;

      // Extract level from cause data
      const levelName = extractLevelName(data.cause?.level);

      // Count by level
      if (levelName === 'streetlight') stats.streetlight++;
      else if (levelName === 'daylight') stats.daylight++;
      else if (levelName === 'sunshield') stats.sunshield++;
      // Default: activities without clear level count as streetlight
      else if (data.cause) stats.streetlight++;
    });

    return stats;
  } catch (error) {
    console.error('Firebase Error: Failed to fetch activity stats.', error);
    return { streetlight: 0, daylight: 0, sunshield: 0, total: 0 };
  }
};

// ─── USER PREFERENCES FUNCTIONS ────────────────────────────────────────────

const PREFERENCE_DEFAULTS = {
  emailUpdatesOptIn: false,
  shareAnonymous: false, // kept in sync with shareNameMode === 'hidden' for older clients
  shareNameMode: null, // 'account' | 'first' | 'custom' | 'hidden'; null = derive from shareAnonymous
  shareCustomName: '',
  isAdultConfirmed: null, // Release 14: true = 18+, false = under 18, null = not asked
  deletionRequested: false,
  deletionRequestedAt: null,
  deletionScheduledDate: null,
};

// Get user preferences document; returns defaults if the document doesn't exist
export const getUserPreferences = async (userId) => {
  try {
    const docRef = doc(db, 'userPreferences', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { ...PREFERENCE_DEFAULTS, ...docSnap.data() };
    }
    return { ...PREFERENCE_DEFAULTS };
  } catch (error) {
    console.error('Firebase Error: Failed to fetch user preferences.', error);
    throw error;
  }
};

// Merge-update the user preferences document
export const updateUserPreferences = async (userId, data) => {
  try {
    const docRef = doc(db, 'userPreferences', userId);
    await setDoc(docRef, data, { merge: true });
  } catch (error) {
    console.error('Firebase Error: Failed to update user preferences.', error);
    throw error;
  }
};

// ─── DATA EXPORT & ACCOUNT DELETION FUNCTIONS ──────────────────────────────

const MILESTONE_NAMES = {
  first_activity: 'First Step Taken',
  first_photo: 'Picture Perfect',
  first_share: 'Spreading the Light',
  first_reflection: 'Deep Thinker',
  five_activities: 'Building Momentum',
  ten_activities: 'Consistent Advocate',
  twenty_five_activities: 'Dedicated Advocate',
  first_elevation: 'Rising Up',
  multi_cause: 'Many Causes, One Heart',
};

// Build and return a structured JSON export of all user data
export const exportUserData = async (userId) => {
  try {
    const currentUser = auth.currentUser;

    const [causesSnap, activitiesSnap, milestonesSnap, elevationsSnap] = await Promise.all([
      getDocs(query(collection(db, 'userCauses'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'activities'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'milestones'), where('userId', '==', userId))),
      getDocs(query(collection(db, 'elevations'), where('userId', '==', userId))),
    ]);

    const causes = causesSnap.docs.map(d => {
      const data = d.data();
      return {
        title: data.title || '',
        level: data.level?.title || data.level || '',
      };
    });

    const activities = activitiesSnap.docs.map(d => {
      const data = d.data();
      return {
        title: data.title || '',
        cause: data.cause?.title || '',
        level: data.cause?.level?.title || '',
        reflection: data.reflection || '',
        date: data.timestamp?.toDate?.()?.toISOString() || null,
        hasPhoto: !!data.imageUrl,
      };
    });

    const milestones = milestonesSnap.docs.map(d => {
      const data = d.data();
      return {
        key: data.milestoneKey,
        name: MILESTONE_NAMES[data.milestoneKey] || data.milestoneKey,
        date: data.achievedAt?.toDate?.()?.toISOString() || null,
      };
    });

    const elevations = elevationsSnap.docs.map(d => {
      const data = d.data();
      return {
        cause: data.causeName || '',
        from: data.fromLevel || '',
        to: data.toLevel || '',
        date: data.requestedAt?.toDate?.()?.toISOString() || null,
      };
    });

    return {
      exportDate: new Date().toISOString(),
      user: {
        displayName: currentUser?.displayName || '',
        email: currentUser?.email || '',
      },
      causes,
      activities,
      milestones,
      elevations,
    };
  } catch (error) {
    console.error('Firebase Error: Failed to export user data.', error);
    throw error;
  }
};

// Write deletion flags to userPreferences — kicks off the 21-day grace period
export const requestAccountDeletion = async (userId) => {
  try {
    const deletionScheduledDate = new Date();
    deletionScheduledDate.setDate(deletionScheduledDate.getDate() + 21);

    const docRef = doc(db, 'userPreferences', userId);
    await setDoc(docRef, {
      deletionRequested: true,
      deletionRequestedAt: serverTimestamp(),
      deletionScheduledDate: Timestamp.fromDate(deletionScheduledDate),
    }, { merge: true });
  } catch (error) {
    console.error('Firebase Error: Failed to request account deletion.', error);
    throw error;
  }
};

// Clear deletion flags — called when a user logs back in within the grace period
export const cancelAccountDeletion = async (userId) => {
  try {
    const docRef = doc(db, 'userPreferences', userId);
    await setDoc(docRef, {
      deletionRequested: false,
      deletionRequestedAt: deleteField(),
      deletionScheduledDate: deleteField(),
    }, { merge: true });
  } catch (error) {
    console.error('Firebase Error: Failed to cancel account deletion.', error);
    throw error;
  }
};

// ─── TEMPLATE FUNCTIONS ─────────────────────────────────────────────────────

// Get all templates for a user, newest first
export const getUserTemplates = async (userId) => {
  try {
    const q = query(
      collection(db, 'templates'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Firebase Error: Failed to fetch user templates.', error);
    throw error;
  }
};

// Save a new template
export const saveTemplate = async (templateData) => {
  try {
    const dataWithTimestamp = { ...templateData, createdAt: serverTimestamp() };
    const docRef = await addDoc(collection(db, 'templates'), dataWithTimestamp);
    return { id: docRef.id, ...templateData };
  } catch (error) {
    console.error('Firebase Error: Failed to save template.', error);
    throw error;
  }
};

// Delete a template by ID
export const deleteTemplate = async (templateId) => {
  try {
    await deleteDoc(doc(db, 'templates', templateId));
    return { success: true };
  } catch (error) {
    console.error('Firebase Error: Failed to delete template.', error);
    throw error;
  }
};

// Get the count of templates for a user
export const getUserTemplateCount = async (userId) => {
  try {
    const q = query(
      collection(db, 'templates'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Firebase Error: Failed to count user templates.', error);
    throw error;
  }
};

// ─── MONTHLY GOALS FUNCTIONS ─────────────────────────────────────────────────

// Fetch the user's monthly goals document
export const getMonthlyGoals = async (userId) => {
  try {
    const docRef = doc(db, 'monthlyGoals', userId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Firebase Error: Failed to fetch monthly goals.', error);
    throw error;
  }
};

// Save or update a single cause's goal target for the current month
export const saveMonthlyGoal = async (userId, causeTitle, goalNumber, currentMonth) => {
  try {
    const docRef = doc(db, 'monthlyGoals', userId);
    const docSnap = await getDoc(docRef);
    const existingGoals = docSnap.exists() ? (docSnap.data().goals || {}) : {};
    const updatedGoals = { ...existingGoals, [causeTitle]: goalNumber };
    await setDoc(docRef, {
      userId,
      month: currentMonth,
      updatedAt: serverTimestamp(),
      goals: updatedGoals,
    }, { merge: true });
  } catch (error) {
    console.error('Firebase Error: Failed to save monthly goal.', error);
    throw error;
  }
};

// Archive completed month and roll goals forward to new month
export const archiveAndRollForwardGoals = async (userId, previousMonth, previousGoals, previousCounts, newMonth) => {
  try {
    const docRef = doc(db, 'monthlyGoals', userId);
    await setDoc(docRef, {
      month: newMonth,
      updatedAt: serverTimestamp(),
      goals: previousGoals,
      goalHistory: arrayUnion({
        month: previousMonth,
        goals: previousGoals,
        counts: previousCounts,
        archivedAt: new Date(),
      }),
    }, { merge: true });
  } catch (error) {
    console.error('Firebase Error: Failed to archive and roll forward goals.', error);
    throw error;
  }
};

// Get activity counts per cause for a given month range
export const getMonthlyActivityCounts = async (userId, causeTitles, monthStart, monthEnd) => {
  try {
    const q = query(
      collection(db, 'activities'),
      where('userId', '==', userId),
      where('timestamp', '>=', monthStart),
      where('timestamp', '<', monthEnd)
    );
    const snapshot = await getDocs(q);
    const counts = {};
    causeTitles.forEach(title => { counts[title] = 0; });
    snapshot.docs.forEach(d => {
      const causeTitle = d.data().cause?.title;
      if (causeTitle && counts.hasOwnProperty(causeTitle)) {
        counts[causeTitle]++;
      }
    });
    return counts;
  } catch (error) {
    console.error('Firebase Error: Failed to fetch monthly activity counts.', error);
    throw error;
  }
};

// ─── ANALYTICS FUNCTIONS ─────────────────────────────────────────────────────

// Get all activities for a user within a date range
export const getActivitiesInRange = async (userId, startDate, endDate) => {
  try {
    const q = query(
      collection(db, 'activities'),
      where('userId', '==', userId),
      where('timestamp', '>=', Timestamp.fromDate(startDate)),
      where('timestamp', '<', Timestamp.fromDate(endDate)),
      orderBy('timestamp', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Firebase Error: Failed to fetch activities in range.', error);
    throw error;
  }
};

// Get total share count for a user
export const getUserShareCount = async (userId) => {
  try {
    const q = query(
      collection(db, 'shares'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Firebase Error: Failed to fetch share count.', error);
    return 0;
  }
};

// Get total elevation count for a user
export const getUserElevationCount = async (userId) => {
  try {
    const q = query(
      collection(db, 'elevations'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.size;
  } catch (error) {
    console.error('Firebase Error: Failed to fetch elevation count.', error);
    return 0;
  }
};

// Get all activities for a user (full count, no pagination)
export const getAllUserActivities = async (userId) => {
  try {
    const q = query(
      collection(db, 'activities'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Firebase Error: Failed to fetch all user activities.', error);
    throw error;
  }
};

// ─── DOCUMENT FUNCTIONS ─────────────────────────────────────────────────────

// Upload document to Firebase Storage
export const uploadDocument = async (userId, activityId, file) => {
  const storageRef = ref(storage, `documents/${userId}/${activityId}/${file.name}`);
  const snapshot = await uploadBytes(storageRef, file);
  const downloadUrl = await getDownloadURL(snapshot.ref);
  return { url: downloadUrl, name: file.name };
};

// Delete document from Firebase Storage
export const deleteDocument = async (userId, activityId, fileName) => {
  const storageRef = ref(storage, `documents/${userId}/${activityId}/${fileName}`);
  try {
    await deleteObject(storageRef);
  } catch (error) {
    // File may already be deleted or not exist — don't throw
    console.warn('Document deletion warning:', error.message);
  }
};

// ─── ACTIVITY EDIT/DELETE FUNCTIONS ──────────────────────────────────────────

// Update an existing activity
export const updateActivity = async (activityId, updatedFields) => {
  try {
    const activityRef = doc(db, 'activities', activityId);
    await updateDoc(activityRef, updatedFields);
    return { success: true };
  } catch (error) {
    console.error('Firebase Error: Failed to update activity.', error);
    throw error;
  }
};

// Permanently delete an activity document from Firestore
export const deleteActivity = async (activityId) => {
  try {
    const activityRef = doc(db, 'activities', activityId);
    await deleteDoc(activityRef);
    return { success: true };
  } catch (error) {
    console.error('Firebase Error: Failed to delete activity.', error);
    throw error;
  }
};

// Delete Storage files associated with an activity (photos + share images)
export const deleteActivityStorageFiles = async (userId, activityId, imageUrl, thumbnailUrl, activity) => {
  try {
    // Helper: extract Storage path from a Firebase download URL
    const getPathFromUrl = (url) => {
      if (!url) return null;
      try {
        // Firebase download URLs encode the path after /o/ and before ?
        const decodedUrl = decodeURIComponent(url);
        // Match the activities/userId/filename or shareImages/userId/filename pattern
        const activityMatch = decodedUrl.match(/(activities\/[^?]+)/);
        if (activityMatch) return activityMatch[1];
        const shareMatch = decodedUrl.match(/(shareImages\/[^?]+)/);
        if (shareMatch) return shareMatch[1];
        return null;
      } catch (e) {
        return null;
      }
    };

    // Delete full-size photo if it exists
    if (imageUrl) {
      const path = getPathFromUrl(imageUrl);
      if (path) {
        try {
          const fileRef = ref(storage, path);
          await deleteObject(fileRef);
        } catch (e) {
          console.warn('Could not delete full-size image:', e.code);
        }
      }
    }

    // Delete thumbnail if it exists
    if (thumbnailUrl) {
      const path = getPathFromUrl(thumbnailUrl);
      if (path) {
        try {
          const fileRef = ref(storage, path);
          await deleteObject(fileRef);
        } catch (e) {
          console.warn('Could not delete thumbnail:', e.code);
        }
      }
    }

    // Delete any share images for this activity
    try {
      const shareImagesRef = ref(storage, `shareImages/${userId}`);
      const shareList = await listAll(shareImagesRef);
      const activityShareImages = shareList.items.filter(item =>
        item.name.includes(`share_${activityId}_`)
      );
      for (const item of activityShareImages) {
        await deleteObject(item);
      }
    } catch (e) {
      console.warn('Could not clean up share images:', e.code);
    }

    // Delete document if exists
    if (activity && activity.documentUrl && activity.documentName) {
      await deleteDocument(activity.userId || userId, activityId, activity.documentName);
    }

    return { success: true };
  } catch (error) {
    console.error('Firebase Error: Failed to delete activity storage files.', error);
    throw error;
  }
};

// ─── ADMIN DELETION FUNCTIONS ────────────────────────────────────────────────

// Get all users with pending deletion requests (admin only)
export const getPendingDeletions = async () => {
  try {
    const q = query(
      collection(db, 'userPreferences'),
      where('deletionRequested', '==', true)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error('Firebase Error: Failed to fetch pending deletions.', error);
    throw error;
  }
};

// Purge all Firestore data for a user (admin only)
// Does NOT delete Firebase Auth account — that requires the Cloud Function
export const purgeUserData = async (userId) => {
  try {
    const collectionsToClean = [
      { name: 'activities', field: 'userId' },
      { name: 'userCauses', field: 'userId' },
      { name: 'templates', field: 'userId' },
      { name: 'milestones', field: 'userId' },
      { name: 'elevations', field: 'userId' },
      { name: 'shares', field: 'userId' },
      { name: 'feedback', field: 'userId' },
    ];

    let totalDeleted = 0;

    for (const col of collectionsToClean) {
      const q = query(
        collection(db, col.name),
        where(col.field, '==', userId)
      );
      const snapshot = await getDocs(q);
      for (const docSnap of snapshot.docs) {
        await deleteDoc(doc(db, col.name, docSnap.id));
        totalDeleted++;
      }
    }

    // Delete single-document collections keyed by userId
    const singleDocs = ['monthlyGoals', 'userPreferences', 'users'];
    for (const colName of singleDocs) {
      try {
        await deleteDoc(doc(db, colName, userId));
        totalDeleted++;
      } catch (e) {
        // Document may not exist, that's fine
      }
    }

    return { success: true, totalDeleted };
  } catch (error) {
    console.error('Firebase Error: Failed to purge user data.', error);
    throw error;
  }
};

// Delete user's files from Firebase Storage (admin only)
export const purgeUserStorage = async (userId) => {
  try {
    const { listAll: listAllFiles, deleteObject } = await import('firebase/storage');

    // Delete activity photos
    const activitiesRef = ref(storage, `activities/${userId}`);
    try {
      const activitiesList = await listAllFiles(activitiesRef);
      for (const item of activitiesList.items) {
        await deleteObject(item);
      }
    } catch (e) {
      // Folder may not exist
    }

    // Delete share images
    const sharesRef = ref(storage, `shareImages/${userId}`);
    try {
      const sharesList = await listAllFiles(sharesRef);
      for (const item of sharesList.items) {
        await deleteObject(item);
      }
    } catch (e) {
      // Folder may not exist
    }

    return { success: true };
  } catch (error) {
    console.error('Firebase Error: Failed to purge user storage.', error);
    throw error;
  }
};
